import React, { useCallback, useEffect, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import ReactTooltip from 'react-tooltip';
import { connect, useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { Div, Notification, Text, Icon } from '@pinpoint/design-kit';

import CONSTANTS from 'src/store/constants';
import { appActions } from 'src/store/actions';

const notificationSelector = (state) => state.app?.notifications;
const messageSelector = (state) => state.app?.message;

const hasNotificationSelector = createSelector(
  [notificationSelector, messageSelector],
  (notifications, message) => !!notifications?.length || !!message
);

const notificationMessageSelector = createSelector(
  [hasNotificationSelector, notificationSelector, messageSelector],
  (hasNotification, notifications, message) => {
    if (hasNotification) {
      if (message) {
        return { ...message, source: 'MESSAGE' };
      }
      return { ...notifications[0], source: 'NOTIFICATION' };
    }
    return null;
  }
);

export function Base({ children }) {
  const dispatch = useDispatch();
  const hasClients = useSelector((state) => !!state.app?.clients?.length);
  const hasNotification = useSelector(hasNotificationSelector);
  const notificationMessage = useSelector(notificationMessageSelector);
  const originalTenant = useSelector((state) => state.app?.profile?.originalTenant);
  const dequeueNotification = useCallback(() => dispatch(appActions.dequeueNotification()), [dispatch]);
  const hideMessage = useCallback(() => dispatch(appActions.hideMessage()), [dispatch]);

  const dismissNotification = useCallback(() => {
    const { source } = notificationMessage;
    if (source === 'MESSAGE') {
      hideMessage();
    } else {
      dequeueNotification();
    }
  }, [dequeueNotification, hideMessage, notificationMessage]);

  useEffect(() => {
    if (hasNotification) {
      const {
        options: { autoClose = false, timeout = 2000 },
      } = notificationMessage;
      if (autoClose) {
        setTimeout(() => {
          dismissNotification();
        }, timeout);
      }
    }
  }, [hasNotification, dismissNotification, notificationMessage]);

  useLayoutEffect(() => {
    ReactTooltip.rebuild();
  });

  return (
    <>
      <ReactTooltip className="pinpoint-tooltip" delayShow={500} multiline />

      <Notification
        align="center"
        backdropFilter="blur(32px)"
        bg="black"
        d="flex"
        isOpen={hasNotification}
        lineHeight={1.4}
        onClose={() => null}
        opacity={0.6}
        position="fromTopRight"
        prefix={notificationMessage?.options?.loading ? <Icon color="white" name="Loading" size="16px" /> : null}
        rounded="sm"
        suffix={
          <Icon
            color="white"
            cursor="pointer"
            name="Cross"
            onClick={dismissNotification}
            pos="absolute"
            right="1rem"
            size="16px"
            top="1.5rem"
          />
        }
        textWeight={400}
        w="32rem"
      >
        <Text m="1rem" opacity={1} p={{ x: '0.5rem' }} textColor="white" textSize="subheader">
          {hasNotification ? notificationMessage.message : ''}
        </Text>
      </Notification>

      {![null, undefined, '--ANONYMOUS--'].includes(originalTenant) && hasClients ? children : null}
    </>
  );
}

function FlyoverPageComponent({ app: { flyoverPinStatus, flyoverOpenStatus }, children }) {
  return (
    <Base>
      <Div
        data-debug="flyover-page-wrapper"
        m={{ x: '0', y: '0' }}
        minH="calc(100% - 4rem)"
        rounded="md"
        transition="all 0.3s ease-in-out"
        w={
          flyoverPinStatus === CONSTANTS.FLYOVER.PINNED && flyoverOpenStatus === CONSTANTS.FLYOVER.OPEN
            ? 'calc(100% - 30vw)'
            : '100%'
        }
      >
        {children}
      </Div>
    </Base>
  );
}

FlyoverPageComponent.propTypes = {
  app: PropTypes.shape({
    flyoverOpenStatus: PropTypes.string.isRequired,
    flyoverPinStatus: PropTypes.string.isRequired,
  }).isRequired,
};

const flyoverPageMapStateToProps = ({ app }) => ({ app });

const PageContainer = connect(flyoverPageMapStateToProps)(FlyoverPageComponent);

export function FlyoverPage(Component) {
  return function Wrapped(props) {
    return (
      <PageContainer>
        <Component {...props} />
      </PageContainer>
    );
  };
}

export function Page(Component) {
  return function Wrapped(props) {
    return (
      <Base>
        <Div
          data-debug="base-cntr"
          h="calc(100% - 2rem)"
          m={{ x: '0', y: '0' }}
          minH="calc(100% - 2rem)"
          rounded="md"
          w="100%"
        >
          <Component {...props} />
        </Div>
      </Base>
    );
  };
}

export function GraphicPage(Component) {
  return function Wrapped(props) {
    return (
      <Base>
        <Div h="100%" maxH="100%" overflow="visible" rounded="md" w="100%">
          <Component {...props} />
        </Div>
      </Base>
    );
  };
}
